import { formatMessageText } from '@/helpers/string.helpers'
import { readableOperatorColor } from './colors'

type Appearance = { id: string; name: string; color: string }
export type AppearanceIndex = ReturnType<typeof buildAppearanceIndex>
export function buildAppearanceIndex(operators: Appearance[] = []) {
  const validOperators = Array.isArray(operators)
    ? operators.filter(operator => operator && typeof operator.id === 'string' && typeof operator.name === 'string')
    : []
  const byId = new Map(validOperators.map(operator => [operator.id, operator]))
  const byName = new Map<string, Appearance | null>()
  for (const operator of validOperators) {
    byName.set(operator.name, byName.has(operator.name) ? null : operator)
  }
  return { byId, byName }
}

export function formatSignedMessageText(
  text: string,
  message: { fromMe?: boolean; senderId?: string; operator?: { id?: string; name?: string } },
  index: AppearanceIndex,
  background: string,
  signedLabel = 'Assinada'
) {
  const html = formatMessageText(text)
  if (!message?.fromMe || typeof text !== 'string' || !text) return html
  // Only an exact, leading operator signature is colored; never arbitrary bold text.
  const signature = text.match(/^\*([^*\n]+):\*/)?.[1]
  if (!signature) return html
  const byId = index.byId.get(message.operator?.id || message.senderId)
  const operator =
    byId && (byId.name === signature || message.operator?.name === signature)
      ? byId
      : !byId
      ? index.byName.get(signature)
      : null
  // Operator metadata is already stored with sent messages. The optional color
  // endpoint must not decide whether the signature header is displayed.
  const hasStoredSignature = Boolean(message.operator?.id && message.operator?.name === signature)
  if (!operator && !hasStoredSignature) return html
  const color = operator ? readableOperatorColor(operator.color, background) : 'inherit'
  const prefix = formatMessageText(`*${signature}:*`)
  return html.startsWith(prefix)
    ? `<span class="message-signature" style="color:${color}"><b class="message-signature-name">${prefix.slice(
        3,
        -5
      )}</b><span class="message-signature-label"><span aria-hidden="true">✎</span> ${formatMessageText(
        signedLabel
      )}</span></span>${html.slice(prefix.length).replace(/^[ \t]*\r?\n/, '')}`
    : html
}
